import axios from 'axios';
import camelcaseKeys from 'camelcase-keys';
import { DateTime } from 'luxon';

import {
  AgentConversationInteractionsResponse,
  AgentConversationsResponse,
  AgentEntry,
  AgentStreamRequest,
  AiRecommendation,
  aiRecommendationFromJson,
  BlockUser,
  ClientConfiguration,
  ConfigCPFR,
  ConfigOSA,
  ConfigurationLink,
  ConnectorMetadataDto,
  CredentialSummaryItem,
  DeleteUser,
  ExpiringCredential,
  FeedbackResponse,
  FileDownloadUrlResponse,
  GuidedJourneyRaw,
  IntegrationPagination,
  MasterUploadRequest,
  OnboardingConfigResponse,
  Options,
  PlanOption,
  PostSupplier,
  PresignedUrlResponse,
  ProviderCredential,
  SiebelLookupResponse,
  SupplierAddonsResponse,
  UpdateFeedbackPayload,
  UpdateSupplierAddons,
  UpdateSupplierPlan,
  UpdateUser,
  UserPagination,
  UserPreferences,
  UserPreferencesRequest,
  UserProvider,
} from '@Models';
import { getAccessToken } from '@Store';
import config from '../config';
import { streamAgentNDJSON, streamAgentSSE, StreamEvent } from '../CustomHooks/AgentStreaming/streamingHelpers';
import { snakeCaseKeys } from './helpers';

const BASEAPI_ADDRESS = config.REACT_APP_BASE_API_ADDRESS;

const bopsApi = ((baseApiAddress: string) => {
  const api = axios.create({
    baseURL: baseApiAddress,
    headers: {
      'Cache-Control': 'no-cache',
      'Content-Type': 'application/json',
    },
    timeout: Number(config.REACT_APP_API_TIMEOUT_MS) || 120000,
  });
  // MIDDLEWARE
  // onRequest
  api.interceptors.request.use(
    async (configuration) => {
      const accessToken = await getAccessToken();

      const updatedConfig = configuration;
      updatedConfig.headers.Authorization = `Bearer ${accessToken}`;

      return updatedConfig;
    },
    (error) => {
      return Promise.reject(error);
    },
  );
  /*
  //onResponse
  instance.interceptors.response.use(
    response => {
      // do custom thing for every response
      return response
    },
    error => {
      return Promise.reject(error)
    }
  )
  */

  return {
    uploadLocationMaster: (file: File, file_type: string) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post(`configuration_gs1/upload_location_master/${file_type}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    downloadChatbotLocationMaster: () => {
      return api.get('configuration_gs1/download_chatbot_location_master', {
        responseType: 'blob',
      });
    },
    getConfigurationLinksGS1: async () => {
      const response = await api.get<ConfigurationLink[]>('configuration_gs1/info/links');
      return response.data;
    },
    requestDownloadConfigurationFile: (fileType: string) => {
      return api.get(`configuration/file/presigned/${fileType}`);
    },
    requestUploadConfigurationFile: (
      fileType: string,
      contentType: string,
      extension: string,
      clientName: string | null = null,
      connector: string | null = null,
      event: 'new_master' | 'update_master' = 'update_master',
    ) => {
      return api.post<PresignedUrlResponse>(`configuration/file/presigned/${fileType}`, {
        file_extension: extension,
        content_type: contentType,
        client_name: clientName,
        connector_name: connector,
        event,
      });
    },
    postNotification: async (notification) => {
      await api.post('configuration/notify-platform-upload', notification);
    },
    getPipelineLastUpdate: async (): Promise<string> => {
      const response = await api.get('pipelineruns/lastUpdate');
      const { data } = response;

      const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
      const datetime = DateTime.fromISO(data).setZone(timeZone);

      return datetime.toLocaleString(DateTime.DATETIME_FULL);
    },
    getDashboard: async (id = ''): Promise<string> => {
      const url = id ? `dashboard/dashboard_embedded?id=${id}` : 'dashboard/dashboard_embedded';

      const response = await api.get(url);
      const { data } = response;

      return String(data);
    },
    getAmazonQEmbedded: async (): Promise<{ embedUrl: string; agentId: string }> => {
      const response = await api.get(`dashboard/amazon_q_embedded`);
      const { data } = response;

      return { embedUrl: String(data.embedUrl), agentId: String(data.agentId) };
    },
    getAiRecommendationByProductCode: async (productCode: string): Promise<AiRecommendation> => {
      const response = await api.get(`recommendations/ai?product_codes=${productCode}`);
      const { data } = response;
      let singleRec;

      if (Array.isArray(data)) singleRec = data[0]; // eslint-disable-line prefer-destructuring

      return aiRecommendationFromJson(singleRec || data);
    },
    getDocumentationPage: async (pageId: string): Promise<any> => {
      const response = await api.get(`resources/${pageId}`);
      const { data } = response;
      return data;
    },
    getDocumentationDatabase: async (databaseId: string): Promise<any> => {
      const response = await api.get(`resources/database/${databaseId}`);
      const { data } = response;
      return data;
    },
    getClientConfiguration: async (): Promise<ClientConfiguration> => {
      const response = await api.get('configuration');
      const { data } = response;
      return camelcaseKeys(data, { deep: true });
    },
    getCredentials: async (): Promise<ProviderCredential[]> => {
      const response = await api.get(`credentials`);
      return response.data.map((credential: any) => camelcaseKeys(credential));
    },
    getCredentialsSummary: async (): Promise<CredentialSummaryItem[]> => {
      const response = await api.get('credentials-summary');
      return response.data.map((item: any) => camelcaseKeys(item, { deep: true }));
    },
    getConnectors: async (): Promise<ConnectorMetadataDto[] | null> => {
      try {
        const response = await api.get('connectors');
        return response.data.map((connector: any) => camelcaseKeys(connector, { deep: true }));
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    postUpdateCredentials: async (credential: ProviderCredential): Promise<void> => {
      await api.post('credentials', snakeCaseKeys(credential));
    },
    postCreateCredentials: async (credential: ProviderCredential): Promise<void> => {
      await api.post('credentials/new', snakeCaseKeys(credential));
    },
    getMFACode: async (id: string): Promise<string> => {
      const response = await api.get(`mfa/code?id=${id}`);
      const { data } = response;
      return data;
    },
    getPhonesMfa: async (): Promise<[]> => {
      const response = await api.get('mfa/phones');
      return response.data;
    },
    buildUploadLink: async (
      clientName: string,
      fileName: string,
      contentType: string,
      retailer: string,
    ): Promise<string> => {
      const response = await api.post('manualUpload', {
        client_name: clientName,
        filename: fileName,
        content_type: contentType,
        retailer,
      });
      const { data } = response;
      return data;
    },
    reportFileUploaded: async (
      clientName: string,
      fileName: string,
      contentType: string,
      retailer: string,
    ): Promise<void> => {
      await api.post('manualUpload/uploaded', {
        client_name: clientName,
        filename: fileName,
        content_type: contentType,
        retailer,
      });
    },
    getUsers: async (
      filters: string,
      currentPage: number,
      pageSize: number,
      field: string | null,
      order: boolean | null,
    ): Promise<UserPagination> => {
      let query = `users?${filters}&page=${currentPage}&limit=${pageSize}`;
      if (field) {
        query += `&sort=${field}:${order ? '-1' : '1'}`;
      }
      const response = await api.get(query);
      return response.data;
    },
    getIntegrations: async (
      filters: string,
      currentPage: number,
      pageSize: number,
      field: string | null,
      order: boolean | null,
    ): Promise<IntegrationPagination> => {
      let query = `integrations?${filters}&page=${currentPage}&size=${pageSize}`;
      if (field) {
        query += `&sort=${field}:${order ? '-1' : '1'}`;
      }
      const response = await api.get(query);
      return response.data;
    },
    postUser: async (userData: UserProvider): Promise<any> => {
      await api.post('users', userData);
    },
    blockUnblockUser: async (data: BlockUser): Promise<any> => {
      await api.patch(`users/email/${data.email}`, {
        blocked: data.blocked,
        client_id: data.clientId,
        user_tags: data.tags,
      });
    },
    changeAccountUser: async (data: UpdateUser): Promise<any> => {
      await api.post(`users/email/${data.email}/changeAccount`, {
        new_client: data.newClient,
        old_client: data.oldClient,
        old_tags: data.currentTags,
        new_tags: data.newTags,
      });
    },
    updateUserTags: async (email: string, clientId: string, newTags: string[]): Promise<any> => {
      await api.post(`users/email/${email}/updateTags`, { client_id: clientId, new_tags: newTags });
    },
    getAvailablePermissions: async (): Promise<{ value: string; description: string }[]> => {
      const response = await api.get('users/permissions');
      return response.data;
    },
    getUserPermissions: async (email: string): Promise<string[]> => {
      const response = await api.get(`users/email/${email}/permissions`);
      return response.data;
    },
    updateUserPermissions: async (email: string, permissions: string[]): Promise<void> => {
      await api.post(`users/email/${email}/updatePermissions`, { permissions });
    },
    changePasswordUser: async (email: string): Promise<any> => {
      await api.post(`users/email/${email}/changePassword`);
    },
    getClients: async (): Promise<Options[]> => {
      const response = await api.get('clients');
      return response.data;
    },
    getSources: async (): Promise<Options[]> => {
      const response = await api.get('clients/sources');
      return response.data;
    },
    getClientTags: async (clientId: string): Promise<string[]> => {
      const response = await api.get(`clients/clientTags/${clientId}`);
      return response.data;
    },
    getSuppliers: async (): Promise<[]> => {
      const response = await api.get('suppliers');
      return response.data;
    },
    postSupplier: async (provider: PostSupplier): Promise<any> => {
      await api.post('suppliers', provider);
    },
    getSupplierPlanOptions: async (): Promise<PlanOption[]> => {
      const response = await api.get('suppliers/plan-options');
      return response.data;
    },
    getSiebelLookup: async (gln: string): Promise<SiebelLookupResponse> => {
      const response = await api.get(`suppliers/${gln}/siebel-lookup`);
      return response.data;
    },
    getSupplierAddons: async (supplierId: string): Promise<SupplierAddonsResponse> => {
      const response = await api.get(`suppliers/${supplierId}/addons`);
      return response.data;
    },
    updateSupplierPlan: async (supplierId: string, data: UpdateSupplierPlan): Promise<{ applied: boolean }> => {
      const response = await api.put(`suppliers/${supplierId}/plan`, data);
      return response.data;
    },
    updateSupplierAddons: async (supplierId: string, data: UpdateSupplierAddons): Promise<void> => {
      await api.patch(`suppliers/${supplierId}/addons`, data);
    },
    getConfigurationLinks: async () => {
      const response = await api.get<ConfigurationLink[]>('configuration/info/links');
      return response.data;
    },
    getManualUpload: async (retail: string): Promise<[]> => {
      const response = await api.get(`manualUpload/retailers/${retail}`);
      return response.data;
    },
    getRetailers: async (): Promise<Options[]> => {
      const response = await api.get('manualUpload/retailers');
      return response.data;
    },
    deleteUser: async (data: DeleteUser): Promise<any> => {
      await api.delete(`users/email/${data.email}`, {
        data: {
          client_id: data.clientId,
          user_tags: data.tags,
        },
      });
    },
    getConfigurationPolicy: async (policyId: string, type: string): Promise<ConfigCPFR | ConfigOSA> => {
      const response = await api.get(`configuration/policy/${type}/${policyId}`);
      return response.data;
    },
    getExpiringCredentials: async (): Promise<ExpiringCredential[]> => {
      const response = await api.get(`credentials/expiring`);
      return response.data;
    },
    validateUser: async (): Promise<any> => {
      const response = await api.post(`users/me`);
      return response.data;
    },
    refreshUser: async (refreshToken: string): Promise<any> => {
      const response = await api.post(`users/me/refresh`, { refresh_token: refreshToken });
      return response.data;
    },
    uploadMasterFile: async (uploadRequest: MasterUploadRequest): Promise<any> => {
      const formData = new FormData();
      formData.append('file', uploadRequest.file);
      formData.append('file_name', uploadRequest.data.file_name);
      formData.append('file_id', uploadRequest.data.file_id);
      formData.append('connector', uploadRequest.data.connector);
      formData.append('event', uploadRequest.data.event);

      const response = await api.post('configuration/master-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },

    /**
     * Streams responses from the I2A agent using Server-Sent Events.
     * This method establishes a streaming connection to the agent runner endpoint
     * and processes incoming text chunks and trace data in real-time.
     *
     * @param request - The agent stream request parameters
     * @param onChunk - Callback function called for each text chunk received
     * @param onTrace - Optional callback function called for trace data
     * @param signal - Optional AbortSignal to cancel the request
     * @throws {Error} When the streaming connection fails or is interrupted
     */
    streamAgent: async (
      request: AgentStreamRequest,
      onChunk: (text: string) => void,
      onTrace?: (trace: unknown) => void,
      signal?: AbortSignal,
    ): Promise<void> => {
      await streamAgentSSE(baseApiAddress, request, onChunk, onTrace, signal);
    },

    /**
     * Streams responses from the I2A agent using NDJSON format.
     * This is a simpler alternative to SSE that returns an AsyncGenerator.
     * Works perfectly with TanStack Query's experimental_streamedQuery.
     *
     * @param request - The agent stream request parameters
     * @param signal - Optional AbortSignal to cancel the request
     * @returns AsyncGenerator that yields stream events
     * @throws {Error} When the streaming connection fails or is interrupted
     */
    streamAgentJSON: (
      request: AgentStreamRequest,
      signal?: AbortSignal,
    ): AsyncGenerator<StreamEvent, void, undefined> => {
      return streamAgentNDJSON(baseApiAddress, request, signal);
    },

    getAgents: async (): Promise<AgentEntry[]> => {
      const response = await api.get<AgentEntry[]>('agents');
      return response.data;
    },

    getGuidedJourneys: async (sourceName?: string): Promise<GuidedJourneyRaw[]> => {
      const params = sourceName ? { source_name: sourceName } : {};
      const response = await api.get<GuidedJourneyRaw[]>('agents/journeys', { params });
      return response.data;
    },

    getAgentConversations: async (limit = 20, sourceName?: string): Promise<AgentConversationsResponse> => {
      const params: Record<string, string | number> = { limit };
      if (sourceName) params.source_name = sourceName;
      const response = await api.get<AgentConversationsResponse>(`agents/conversations`, { params });
      return response.data;
    },

    getAgentConversationInteractions: async (
      conversationId: string,
      limit = 100,
    ): Promise<AgentConversationInteractionsResponse> => {
      const response = await api.get<AgentConversationInteractionsResponse>(`agents/conversations/${conversationId}`, {
        params: { limit },
      });
      return response.data;
    },

    updateInteractionFeedback: async (
      conversationId: string,
      payload: UpdateFeedbackPayload,
    ): Promise<FeedbackResponse> => {
      const response = await api.patch<FeedbackResponse>(`agents/conversations/${conversationId}/feedback`, payload);
      return response.data;
    },

    deleteConversation: async (conversationId: string): Promise<any> => {
      const response = await api.delete(`agents/conversations/${conversationId}`);
      return response.data;
    },

    redeemJourneyToken: async (token: string): Promise<{ conversation_id: string; source_name?: string }> => {
      const response = await api.post<{ conversation_id: string; source_name?: string }>(
        `agents/conversations/from-journey-token/${token}`,
      );
      return response.data;
    },

    updateConversationTitle: async (conversationId: string, title: string): Promise<any> => {
      const response = await api.patch(`agents/conversations/${conversationId}/title`, null, {
        params: { title },
      });
      return response.data;
    },

    getFileDownloadUrl: async (
      conversationId: string,
      timestamp: string,
      fileName: string,
      inline = false,
    ): Promise<FileDownloadUrlResponse> => {
      const response = await api.post(`agents/conversations/${conversationId}/files/download`, {
        timestamp,
        file_name: fileName,
        inline,
      });
      return response.data;
    },

    getUserPreferences: async (sourceName?: string): Promise<UserPreferences> => {
      const params = sourceName ? { source_name: sourceName } : {};
      const response = await api.get('agent/user-preferences/', { params });
      return camelcaseKeys(response.data, { deep: true });
    },

    updateUserPreferences: async (
      preferences: UserPreferencesRequest,
      sourceName?: string,
    ): Promise<UserPreferences> => {
      const params = sourceName ? { source_name: sourceName } : {};
      const response = await api.put('agent/user-preferences/', snakeCaseKeys({ ...preferences }), { params });
      return camelcaseKeys(response.data, { deep: true });
    },

    deleteUserPreferences: async (sourceName?: string): Promise<void> => {
      const params = sourceName ? { source_name: sourceName } : {};
      await api.delete('agent/user-preferences/', { params });
    },

    completeOnboarding: async (sourceName?: string): Promise<void> => {
      const params = sourceName ? { source_name: sourceName } : {};
      await api.post('agent/user-preferences/onboarding-complete', null, { params });
    },

    getOnboardingConfig: async (sourceName?: string): Promise<OnboardingConfigResponse> => {
      const params = sourceName ? { source_name: sourceName } : {};
      const response = await api.get('agents/onboarding-config', { params });
      return camelcaseKeys(response.data, { deep: true }) as OnboardingConfigResponse;
    },

    getChangeLogEntries: async (language?: string): Promise<any> => {
      const params = language ? { language } : {};
      const response = await api.get(`changelog/list`, { params });
      return response.data;
    },
    getChangeLogEntryDetail: async (id: string): Promise<any> => {
      const response = await api.get(`changelog/detail/${id}`);
      return response.data;
    },
    updateClientPatches: async (seenPatches: string[]): Promise<any> => {
      const response = await api.post(`changelog/user`, { seen_patches: seenPatches });
      return response.data;
    },
    notifyNoClient: async (): Promise<void> => {
      await api.post('onboarding/notify-no-client');
    },
    provisionSupplier: async (): Promise<{ client_id: string }> => {
      const response = await api.post('onboarding/provision-supplier');
      return response.data;
    },
  };
})(BASEAPI_ADDRESS);

export default bopsApi;
